/**
 * @file consent-resolve.ts
 * @description «Что показать посетителю» — блок `consent` ответа
 * `GET /public/site-config` из части COUNTERS (phase19.16, партия Р3г,
 * v0.19.62; site-builder.md SB133–SB136; db-design-r3g §2.2 «Решение «что
 * показать посетителю»», §3, §5).
 *
 * Зачем в shared: одна чистая функция для API (сборка site-config по
 * опубликованной ревизии) и кабинета (баннер на канве по черновику). Витрина
 * получает готовые тексты и словарь фраз не знает.
 *
 * Таблица решений (db-design-r3g §2.2):
 *   счётчики есть, режим «после согласия»             → `consent`;
 *   счётчики есть, «Уведомление» разрешено платформой → `notice`;
 *   счётчики есть, «Уведомление» запрещено платформой → `consent`;
 *   счётчиков нет → `info`, если «Без счётчиков показывать уведомление», иначе баннера нет.
 *
 * Ключ согласия `e<epoch>v<consentVersion ?? 0><k>` (k — первая буква вида):
 * сменился — посетителя спрашивают снова. Вид в ключе, поэтому запрет
 * «Уведомления» платформой сам переспрашивает тех, кто «согласился» только
 * уведомлением. `SITE_CONSENT_EPOCH` — константа платформы: её повышение
 * переспрашивает всех посетителей всех магазинов (например, если юрист
 * потребует журнал, OQ-123 (е)).
 */

import {
  COUNTER_SERVICES,
  enabledConsentCategories,
  enabledCounterServices,
  metrikaEcommerceEnabled,
  type SiteConsentCategory,
  type SiteCounterMode,
  type SiteCounters,
} from "../site-theme/counters.js";
import { SITE_CONSENT_KEEP_DAYS } from "./consent-cookie.js";
import { buildConsentBannerText, type ConsentBannerTexts, type SiteConsentBannerKind } from "./consent-texts.js";

/** Эпоха согласий платформы — повышение переспрашивает всех (db-design-r3g §2.2, §8). */
export const SITE_CONSENT_EPOCH = 1;

/** Категория в окне «Настроить» с названиями её сервисов. */
export interface PublicSiteConsentCategoryDto {
  id: SiteConsentCategory;
  label: string;
  description: string;
  services: string[];
}

/** Подписи кнопок и окна — от платформы, по редакции текста. */
export interface PublicSiteConsentUiDto {
  buttons: ConsentBannerTexts["buttons"];
  settingsTitle: string;
  alwaysOn: string;
  more: string;
  settingsLink: string;
  necessary: { label: string; description: string };
}

export interface PublicSiteConsentBannerDto {
  kind: SiteConsentBannerKind;
  /** Ключ условий: не совпал с cookie посетителя — баннер снова. */
  key: string;
  /** Вступление (владельца или платформы); у `info` — текст уведомления. */
  intro: string;
  /** Фраза режима со списком сервисов; у `info` — null. */
  modeText: string | null;
  /** Полный текст полосы и карточки. */
  text: string;
  /** Компактная полоса в 2 строки (/checkout, /order-confirm, ЛК). */
  compactText: string;
  /** Категории окна «Настроить»; у `info` — пусто. */
  categories: PublicSiteConsentCategoryDto[];
  ui: PublicSiteConsentUiDto;
  /** Ссылка «Подробнее» — политика ПДн, если она видна (иначе ссылки нет). */
  policyHref: string | null;
  /** Сроки cookie согласия, дни (SB136). */
  keepDays: { accepted: number; refused: number };
}

/** Блок `consent` в `GET /public/site-config`. */
export interface PublicSiteConsentDto {
  banner: PublicSiteConsentBannerDto | null;
  /** Что грузить после согласия; без счётчиков — оба null. */
  counters: {
    /**
     * `ecommerce` — передавать ли события электронной коммерции (SB147,
     * v0.19.65). Сервер ставит всегда; необязателен в типе на переходный
     * период (разбор витрины до волны 2): нет ключа (старый ответ) — витрина
     * считает `false` и данные не отправляет (db-design-seo-tech §4.5).
     */
    metrika: { id: string; webvisor: boolean; ecommerce?: boolean } | null;
    vk: { id: string } | null;
  };
  /** «Настройки cookie» последним пунктом нижней полосы — когда счётчики есть (SB136). */
  settingsLink: boolean;
}

export interface ResolveSiteConsentOptions {
  /** Разрешён ли режим «Уведомление» платформой (`SITE_CONSENT_NOTICE_ALLOWED`). */
  noticeAllowed: boolean;
  /** `SiteRevision.consentVersion` опубликованной версии; null — условия не менялись с ввода Р3г. */
  consentVersion: number | null;
  /** Адрес политики ПДн, если она видна покупателям; null — без ссылки. */
  policyHref: string | null;
  /**
   * Тексты редакции платформы. API подставляет редакцию по номеру
   * `counters.platformTextVersion` из снимка (номер без строки в базе —
   * текущую, с предупреждением проверки); кабинет — для канвы.
   */
  texts: ConsentBannerTexts;
  /** Эпоха (по умолчанию `SITE_CONSENT_EPOCH`) — параметром для тестов. */
  epoch?: number;
}

/** Режим с учётом запрета платформы: черновик магазина не портится, действует «после согласия». */
export function effectiveCounterMode(counters: Pick<SiteCounters, "mode">, noticeAllowed: boolean): SiteCounterMode {
  return counters.mode === "notice" && noticeAllowed ? "notice" : "consent";
}

/** Ключ согласия `e1v42c`. */
export function siteConsentKey(epoch: number, consentVersion: number | null, kind: SiteConsentBannerKind): string {
  return `e${epoch}v${consentVersion ?? 0}${kind.charAt(0)}`;
}

/** Вид баннера по части COUNTERS (null — баннера нет). */
export function siteConsentBannerKind(counters: SiteCounters, noticeAllowed: boolean): SiteConsentBannerKind | null {
  if (enabledCounterServices(counters).length === 0) return counters.noticeWithoutCounters ? "info" : null;
  return effectiveCounterMode(counters, noticeAllowed);
}

/**
 * Блок `consent` для витрины. Тексты (`opts.texts`) — редакции, с которой
 * опубликован баннер (`platformTextVersion`), а не текущей: посетитель видит
 * ровно то, что записано в версии сайта. Функция чистая — тексты передаёт
 * вызывающий.
 */
export function resolveSiteConsent(counters: SiteCounters, opts: ResolveSiteConsentOptions): PublicSiteConsentDto {
  const services = enabledCounterServices(counters);
  const kind = siteConsentBannerKind(counters, opts.noticeAllowed);
  const out: PublicSiteConsentDto = {
    banner: null,
    counters: {
      metrika:
        counters.metrika === null
          ? null
          : {
              id: counters.metrika.id,
              webvisor: counters.metrika.webvisor,
              ecommerce: metrikaEcommerceEnabled(counters),
            },
      vk: counters.vk === null ? null : { id: counters.vk.id },
    },
    settingsLink: services.length > 0,
  };
  if (kind === null) return out;

  const t = opts.texts;
  const categories = kind === "info" ? [] : enabledConsentCategories(counters);
  const withServices = categories.map((id) => ({
    id,
    services: services.filter((s) => COUNTER_SERVICES[s].category === id).map((s) => COUNTER_SERVICES[s].shortLabel),
  }));
  const text = buildConsentBannerText(t, { kind, intro: counters.intro, categories: withServices });
  out.banner = {
    kind,
    key: siteConsentKey(opts.epoch ?? SITE_CONSENT_EPOCH, opts.consentVersion, kind),
    intro: text.intro,
    modeText: text.modeText,
    text: text.text,
    compactText: text.compactText,
    categories: categories.map((id) => ({
      id,
      label: t.categories[id].label,
      description:
        id === "analytics" && counters.metrika?.webvisor === true
          ? t.analyticsWebvisorDescription
          : t.categories[id].description,
      // В окне «Настроить» — полные названия сервисов категории.
      services: services.filter((s) => COUNTER_SERVICES[s].category === id).map((s) => COUNTER_SERVICES[s].label),
    })),
    ui: {
      buttons: { ...t.buttons },
      settingsTitle: t.settingsTitle,
      alwaysOn: t.alwaysOn,
      more: t.more,
      settingsLink: t.settingsLink,
      necessary: { label: t.necessary.label, description: t.necessary.description },
    },
    policyHref: opts.policyHref,
    keepDays: { ...SITE_CONSENT_KEEP_DAYS },
  };
  return out;
}
